import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../Config/config';
import {
  AcademicCapIcon,
  CpuChipIcon,
  BoltIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  ArrowRightIcon,
  ClockIcon,
  CheckBadgeIcon,
  PlusCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';

// --- constants ----------------------------------------------------------------

const MAX_ENROLLMENTS = 1;

const TRACK_META = {
  techbunch: {
    label:  'Full Stack Engineering Path',
    icon:   BoltIcon,
    gradient: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)',
    iconBg:   'rgba(124,58,237,0.08)',
    iconColor: '#7C3AED',
    badgeBg:  '#F5F3FF',
    badgeText:'#5B21B6',
    barColor: '#7C3AED',
    stripGradient: 'linear-gradient(90deg, #7C3AED, #A855F7)',
  },
  roles: {
    label:  'Industry Career Accelerator',
    icon:   UserGroupIcon,
    gradient: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
    iconBg:   'rgba(5,150,105,0.08)',
    iconColor: '#059669',
    badgeBg:  '#ECFDF5',
    badgeText:'#065F46',
    barColor: '#059669',
    stripGradient: 'linear-gradient(90deg, #059669, #10B981)',
  },
};

const STATUS_META = {
  active:    { label: 'Active',    bg: '#DCFCE7', text: '#14532D', dot: '#16A34A' },
  completed: { label: 'Completed', bg: '#DBEAFE', text: '#1E3A8A', dot: 'var(--primary-blue, #4F46E5)' },
  dropped:   { label: 'Dropped',   bg: '#FEE2E2', text: '#7F1D1D', dot: '#DC2626' },
};

// --- helpers -----------------------------------------------------------------

function getDashboardPath(enrollment, userId) {
  if (!enrollment) return '/';

  // Store enrollment info for dashboards
  if (enrollment.enrollment_id) {
    localStorage.setItem('currentEnrollmentId', enrollment.enrollment_id);
  }

  // Ensure userId is in localStorage
  if (userId) {
    localStorage.setItem('userId', userId);
  }

  // Use track-specific dashboards for a better user experience
  if (enrollment.track === 'technologies') {
    return `/techuserdashboard?enrollmentId=${enrollment.enrollment_id}&userId=${userId}`;
  }
  if (enrollment.track === 'techbunch') {
    return `/techbunchuserdashboard?enrollmentId=${enrollment.enrollment_id}&userId=${userId}`;
  }
  if (enrollment.track === 'certified') {
    return `/certifieduserdashboard?enrollmentId=${enrollment.enrollment_id}&userId=${userId}`;
  }
  if (enrollment.track === 'roles') {
    return enrollment.workMode === 'team' 
      ? `/team-roles?enrollmentId=${enrollment.enrollment_id}&userId=${userId}` 
      : `/individual-roles?enrollmentId=${enrollment.enrollment_id}&userId=${userId}`;
  }

  // Fallback to Unified Dashboard
  return `/bootcamp/dashboard?enrollmentId=${enrollment.enrollment_id}&userId=${userId}`;
}

function formatDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function getUserId() {
  try {
    return (
      localStorage.getItem('user_id') ||
      JSON.parse(localStorage.getItem('user') || '{}')?.userId ||
      ''
    );
  } catch { return ''; }
}

// --- ProgressBar -------------------------------------------------------------

function ProgressBar({ value, barColor }) {
  const pct = Math.min(100, Math.max(0, value || 0));
  return (
    <div
      style={{
        width: '100%',
        height: '6px',
        borderRadius: '99px',
        background: '#F1F5F9',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${pct}%`,
          borderRadius: '99px',
          background: barColor,
          transition: 'width 700ms cubic-bezier(.4,0,.2,1)',
        }}
      />
    </div>
  );
}

// --- BootcampCard -------------------------------------------------------------

function BootcampCard({ enrollment, onOpenDashboard, onSetActive }) {
  const meta   = TRACK_META[enrollment.track] || TRACK_META.techbunch;
  const status = STATUS_META[enrollment.status] || STATUS_META.active;
  const Icon   = meta.icon;
  const [activating, setActivating] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handleOpen = () => onOpenDashboard(enrollment);

  const handleSetActive = async (e) => {
    e.stopPropagation();
    setActivating(true);
    await onSetActive(enrollment.enrollment_id);
    setActivating(false);
  };

  const pct = Math.min(100, Math.max(0, enrollment.progressPercentage || 0));

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Open ${enrollment.skill_track_name} dashboard`}
      onClick={handleOpen}
      onKeyDown={(e) => e.key === 'Enter' && handleOpen()}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '20px',
        background: '#FFFFFF',
        border: enrollment.isActive
          ? '1.5px solid var(--primary-blue, #4F46E5)'
          : '1.5px solid #E2E8F0',
        boxShadow: hovered
          ? '0 8px 32px rgba(15,23,42,0.10)'
          : enrollment.isActive
            ? '0 4px 20px rgba(79, 70, 229,0.12)'
            : '0 2px 8px rgba(15,23,42,0.05)',
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'all 300ms cubic-bezier(.4,0,.2,1)',
        cursor: 'pointer',
        overflow: 'hidden',
      }}
    >
      {/* Top gradient accent strip */}
      <div style={{ height: '4px', background: meta.stripGradient, flexShrink: 0 }} />

      {/* Active badge - top-right corner */}
      {enrollment.isActive && (
        <div
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            background: '#EFF6FF',
            border: '1px solid #BFDBFE',
            color: 'var(--secondary-blue, #1E3A8A)',
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            padding: '3px 10px 3px 7px',
            borderRadius: '99px',
          }}
        >
          <CheckBadgeIcon style={{ width: '13px', height: '13px', color: 'var(--primary-blue, #4F46E5)' }} />
          Active
        </div>
      )}

      {/* Card body */}
      <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '16px', flex: 1 }}>

        {/* Track icon + name */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '12px',
              background: meta.iconBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon style={{ width: '22px', height: '22px', color: meta.iconColor }} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{
              fontSize: '10px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: meta.iconColor,
              marginBottom: '4px',
              lineHeight: 1,
            }}>
              {meta.label}
            </p>
            <h3 style={{
              fontSize: '14px',
              fontWeight: 800,
              color: '#0F172A',
              lineHeight: 1.4,
              margin: 0,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {enrollment.skill_track_name}
            </h3>
          </div>
        </div>

        {/* Badges row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {/* Experience level */}
          <span style={{
            fontSize: '11px',
            fontWeight: 600,
            padding: '3px 10px',
            borderRadius: '99px',
            background: meta.badgeBg,
            color: meta.badgeText,
            border: `1px solid ${meta.badgeBg}`,
            lineHeight: 1.6,
          }}>
            {enrollment.experienceLevel || '0–2 yrs'}
          </span>

          {/* Status badge */}
          <span style={{
            fontSize: '11px',
            fontWeight: 600,
            padding: '3px 10px',
            borderRadius: '99px',
            background: status.bg,
            color: status.text,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            lineHeight: 1.6,
          }}>
            <span style={{
              width: '6px', height: '6px', borderRadius: '50%',
              background: status.dot, flexShrink: 0,
            }} />
            {status.label}
          </span>

          {/* Work mode badge (team only) */}
          {enrollment.workMode && enrollment.workMode !== 'single' && (
            <span style={{
              fontSize: '11px',
              fontWeight: 600,
              padding: '3px 10px',
              borderRadius: '99px',
              background: '#F8FAFC',
              color: '#64748B',
              border: '1px solid #E2E8F0',
              textTransform: 'capitalize',
              lineHeight: 1.6,
            }}>
              {enrollment.workMode}
            </span>
          )}
        </div>

        {/* Progress section */}
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
          }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748B', letterSpacing: '0.03em' }}>
              PROGRESS
            </span>
            <span style={{
              fontSize: '13px',
              fontWeight: 800,
              color: pct === 100 ? '#059669' : meta.iconColor,
            }}>
              {pct}%
            </span>
          </div>
          <ProgressBar value={pct} barColor={pct === 100 ? '#059669' : meta.barColor} />
          {/* Progress stage label */}
          <p style={{
            fontSize: '10px',
            color: '#94A3B8',
            marginTop: '5px',
            fontWeight: 500,
          }}>
            {pct === 0 && enrollment.status !== 'active'
              ? 'Not started yet'
              : pct === 0 && enrollment.status === 'active'
                ? 'Started - Just getting started'
                : pct < 30
                  ? 'Just getting started'
                  : pct < 60
                    ? 'Making good progress'
                    : pct < 90
                      ? 'Almost there!'
                      : pct === 100
                        ? '🎉 Completed!'
                        : 'Final stretch!'}
          </p>
        </div>

        {/* Enrolled date */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 12px',
          borderRadius: '10px',
          background: '#F8FAFC',
          border: '1px solid #F1F5F9',
        }}>
          <ClockIcon style={{ width: '13px', height: '13px', color: '#94A3B8', flexShrink: 0 }} />
          <span style={{ fontSize: '11px', color: '#64748B', fontWeight: 500 }}>
            Enrolled on {formatDate(enrollment.enrolledAt)}
          </span>
        </div>
      </div>

      {/* Card footer - action buttons */}
      <div
        style={{
          padding: '0 22px 20px',
          display: 'flex',
          gap: '8px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Primary: Open Dashboard */}
        <button
          onClick={handleOpen}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            padding: '10px 16px',
            borderRadius: '12px',
            border: 'none',
            background: meta.stripGradient,
            color: '#FFFFFF',
            fontSize: '13px',
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'opacity 150ms, transform 100ms',
            letterSpacing: '0.01em',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.88'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.97)'; }}
          onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
          Open Dashboard
          <ArrowRightIcon style={{ width: '14px', height: '14px' }} />
        </button>

        {/* Secondary: Set Active (only when not active) */}
        {!enrollment.isActive && (
          <button
            onClick={handleSetActive}
            disabled={activating}
            title="Set as active bootcamp"
            style={{
              padding: '10px 14px',
              borderRadius: '12px',
              border: '1.5px solid #E2E8F0',
              background: '#FFFFFF',
              color: '#475569',
              fontSize: '12px',
              fontWeight: 600,
              cursor: activating ? 'not-allowed' : 'pointer',
              opacity: activating ? 0.6 : 1,
              transition: 'all 150ms',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => {
              if (!activating) {
                e.currentTarget.style.borderColor = '#CBD5E1';
                e.currentTarget.style.background = '#F8FAFC';
                e.currentTarget.style.color = '#0F172A';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#E2E8F0';
              e.currentTarget.style.background = '#FFFFFF';
              e.currentTarget.style.color = '#475569';
            }}
          >
            {activating
              ? <ArrowPathIcon style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }} />
              : 'Set Active'}
          </button>
        )}
      </div>
    </div>
  );
}

// --- Empty state --------------------------------------------------------------

function EmptyState({ onExplore }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '72px 24px',
      textAlign: 'center',
    }}>
      {/* Illustration area */}
      <div style={{
        width: '88px',
        height: '88px',
        borderRadius: '24px',
        background: 'linear-gradient(135deg, #EFF6FF 0%, #F5F3FF 100%)',
        border: '1.5px solid #E2E8F0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '24px',
        boxShadow: '0 4px 20px rgba(79, 70, 229,0.08)',
      }}>
        <AcademicCapIcon style={{ width: '40px', height: '40px', color: 'var(--primary-blue, #4F46E5)' }} />
      </div>

      <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0F172A', marginBottom: '8px' }}>
        No Bootcamps Yet
      </h2>
      <p style={{
        fontSize: '14px',
        color: '#64748B',
        maxWidth: '340px',
        lineHeight: 1.7,
        marginBottom: '32px',
      }}>
        You haven't enrolled in any bootcamp yet. Explore structured skill tracks
        designed to make you industry-ready.
      </p>

      <button
        onClick={onExplore}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 24px',
          borderRadius: '14px',
          border: 'none',
          background: 'linear-gradient(135deg, var(--primary-blue, #4F46E5), #7C3AED)',
          color: '#FFFFFF',
          fontSize: '14px',
          fontWeight: 700,
          cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(79, 70, 229,0.25)',
          transition: 'opacity 150ms, transform 100ms',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.88'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
        onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.97)'; }}
        onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        <SparklesIcon style={{ width: '18px', height: '18px' }} />
        Explore Bootcamps
      </button>
    </div>
  );
}

// --- Loading skeleton ---------------------------------------------------------

function SkeletonCard() {
  return (
    <div style={{
      borderRadius: '20px',
      background: '#FFFFFF',
      border: '1.5px solid #E2E8F0',
      overflow: 'hidden',
    }}>
      <div style={{ height: '4px', background: 'linear-gradient(90deg, #E2E8F0, #F1F5F9, #E2E8F0)', backgroundSize: '200% 100%' }} />
      <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#F1F5F9', flexShrink: 0 }} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', paddingTop: '2px' }}>
            <div style={{ height: '10px', borderRadius: '6px', background: '#F1F5F9', width: '55%' }} />
            <div style={{ height: '14px', borderRadius: '6px', background: '#E2E8F0', width: '90%' }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ height: '22px', width: '80px', borderRadius: '99px', background: '#F1F5F9' }} />
          <div style={{ height: '22px', width: '64px', borderRadius: '99px', background: '#F1F5F9' }} />
        </div>
        <div>
          <div style={{ height: '10px', borderRadius: '6px', background: '#F1F5F9', width: '30%', marginBottom: '10px' }} />
          <div style={{ height: '6px', borderRadius: '99px', background: '#F1F5F9' }} />
        </div>
        <div style={{ height: '36px', borderRadius: '10px', background: '#F8FAFC' }} />
        <div style={{ height: '40px', borderRadius: '12px', background: '#F1F5F9' }} />
      </div>
    </div>
  );
}

// --- main component -----------------------------------------------------------

export default function MyBootcamps() {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');

  const userId = getUserId();

  const fetchEnrollments = useCallback(async () => {
    if (!userId) {
      setError('User not logged in. Please log in again.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers = {};
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
        headers['X-Session-Token'] = sessionToken;
      }
      const res = await fetch(
        `${API_BASE_URL}/api/bootcamp/enrollments/my-bootcamps?userId=${encodeURIComponent(userId)}`,
        { headers }
      );
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
      // Filter out enrollments removed by admin or non-active (extra client-side safety)
      const visibleEnrollments = (data.enrollments || []).filter(e => !['REMOVED', 'dropped', 'CANCELLED', 'INACTIVE'].includes(e.status));
      setEnrollments(visibleEnrollments);
    } catch (err) {
      setError(err.message || 'Failed to load enrollments.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchEnrollments(); }, [fetchEnrollments]);

  const handleOpenDashboard = (enrollment) => {
    const path = getDashboardPath(enrollment, userId);
    navigate(path, {
      state: {
        userId,
        enrollmentId:    enrollment.enrollment_id,
        technology:      undefined,
        techBunch:       enrollment.track === 'techbunch'    ? enrollment.skill_track_name : undefined,
        certification:   undefined,
        role:            enrollment.track === 'roles'        ? enrollment.skill_track_name : undefined,
        experienceLevel: enrollment.experienceLevel,
        track:           enrollment.track,
      },
    });
  };

  const handleSetActive = async (enrollmentId) => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers = { 'Content-Type': 'application/json' };
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
        headers['X-Session-Token'] = sessionToken;
      }
      await fetch(`${API_BASE_URL}/api/bootcamp/enrollments/active/${enrollmentId}`, {
        method:  'PUT',
        headers,
        body:    JSON.stringify({ userId }),
      });
      await fetchEnrollments();
    } catch (err) {
      console.error('setActiveBootcamp error:', err);
    }
  };

  const activeCount = enrollments.filter(e => !['dropped', 'REMOVED', 'CANCELLED', 'INACTIVE'].includes(e.status)).length;
  const atLimit     = activeCount >= MAX_ENROLLMENTS;
  const slotsLeft   = MAX_ENROLLMENTS - activeCount;

  // --- render -----------------------------------------------------------------
  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 20px 64px' }}>

        {/* ══════════════════════════════════════
            PAGE HEADER
        ══════════════════════════════════════ */}
        <div style={{ marginBottom: '32px' }}>

          {/* Title row */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '16px',
            marginBottom: '20px',
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                <button 
                  onClick={() => navigate(-1)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: 'white',
                    border: '1.5px solid #E2E8F0',
                    cursor: 'pointer',
                    color: '#64748B'
                  }}
                >
                  <ArrowLeftIcon style={{ width: '18px', height: '18px' }} />
                </button>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  background: 'rgba(79, 70, 229,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <AcademicCapIcon style={{ width: '20px', height: '20px', color: 'var(--primary-blue, #4F46E5)' }} />
                </div>
                <h1 style={{
                  fontSize: '26px',
                  fontWeight: 900,
                  color: '#0F172A',
                  letterSpacing: '-0.02em',
                  margin: 0,
                }}>
                  My Bootcamps
                </h1>
              </div>
              <p style={{ fontSize: '14px', color: '#64748B', margin: 0, fontWeight: 500 }}>
                {loading
                  ? 'Loading your enrollments...'
                  : enrollments.length === 0
                    ? 'No enrollments yet - explore available tracks below'
                    : 'Your active bootcamp enrollment'}
              </p>
            </div>

            {/* Right side: slot pips + enroll button */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
              {/* Slot pip indicators hidden as only 1 is allowed */}
              {!loading && enrollments.length > 0 && false && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '8px 14px',
                  borderRadius: '99px',
                  background: '#FFFFFF',
                  border: '1.5px solid #E2E8F0',
                }}>
                  {Array.from({ length: MAX_ENROLLMENTS }).map((_, i) => (
                    <div
                      key={i}
                      title={i < activeCount ? 'Slot used' : 'Slot available'}
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: i < activeCount ? 'var(--primary-blue, #4F46E5)' : '#E2E8F0',
                        transition: 'all 200ms',
                      }}
                    />
                  ))}
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: atLimit ? '#DC2626' : '#64748B',
                    marginLeft: '4px',
                    whiteSpace: 'nowrap',
                  }}>
                    {atLimit ? 'Full' : `${slotsLeft} left`}
                  </span>
                </div>
              )}

              {/* Enroll button */}
              <button
                onClick={() => navigate('/bootcamp/enrollment')}
                disabled={atLimit}
                title={atLimit ? `Maximum ${MAX_ENROLLMENTS} enrollments reached` : 'Enroll in a new bootcamp'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '7px',
                  padding: '10px 18px',
                  borderRadius: '12px',
                  border: atLimit ? '1.5px solid #E2E8F0' : 'none',
                  background: atLimit
                    ? '#F8FAFC'
                    : 'linear-gradient(135deg, var(--primary-blue, #4F46E5), #7C3AED)',
                  color: atLimit ? '#94A3B8' : '#FFFFFF',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: atLimit ? 'not-allowed' : 'pointer',
                  boxShadow: atLimit ? 'none' : '0 4px 14px rgba(79, 70, 229,0.25)',
                  transition: 'opacity 150ms, transform 100ms',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => { if (!atLimit) e.currentTarget.style.opacity = '0.88'; }}
                onMouseLeave={(e) => { if (!atLimit) e.currentTarget.style.opacity = '1'; }}
                onMouseDown={(e) => { if (!atLimit) e.currentTarget.style.transform = 'scale(0.97)'; }}
                onMouseUp={(e) => { if (!atLimit) e.currentTarget.style.transform = 'scale(1)'; }}
              >
                <PlusCircleIcon style={{ width: '16px', height: '16px' }} />
                {atLimit ? 'Limit Reached' : 'New Enrollment'}
              </button>
            </div>
          </div>

          {/* -- Enrollment progress bar hidden for student tenant -- */}
          {!loading && enrollments.length > 0 && false && (
            <div style={{
              padding: '14px 18px',
              borderRadius: '14px',
              background: '#FFFFFF',
              border: '1.5px solid #E2E8F0',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
            }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748B', whiteSpace: 'nowrap' }}>
                SLOTS
              </span>
              <div style={{ flex: 1, height: '6px', borderRadius: '99px', background: '#F1F5F9', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${(activeCount / MAX_ENROLLMENTS) * 100}%`,
                  borderRadius: '99px',
                  background: atLimit
                    ? 'linear-gradient(90deg, #F59E0B, #EF4444)'
                    : 'linear-gradient(90deg, var(--primary-blue, #4F46E5), #7C3AED)',
                  transition: 'width 700ms cubic-bezier(.4,0,.2,1)',
                }} />
              </div>
              <span style={{
                fontSize: '12px',
                fontWeight: 700,
                color: atLimit ? '#DC2626' : 'var(--primary-blue, #4F46E5)',
                whiteSpace: 'nowrap',
              }}>
                {activeCount} / {MAX_ENROLLMENTS}
              </span>
            </div>
          )}
        </div>

        {/* ══════════════════════════════════════
            LIMIT REACHED ALERT
        ══════════════════════════════════════ */}
        {atLimit && !loading && (
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '14px',
            padding: '16px 20px',
            borderRadius: '16px',
            background: '#FFFBEB',
            border: '1.5px solid #FDE68A',
            marginBottom: '24px',
          }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: '#FEF3C7',
              border: '1px solid #FDE68A',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <ExclamationTriangleIcon style={{ width: '18px', height: '18px', color: '#D97706' }} />
            </div>
            <div>
              <p style={{ fontSize: '14px', fontWeight: 700, color: '#92400E', margin: '0 0 3px' }}>
                Enrollment limit reached
              </p>
              <p style={{ fontSize: '13px', color: '#B45309', margin: 0, lineHeight: 1.6, fontWeight: 500 }}>
                You've used all {MAX_ENROLLMENTS} enrollment slots. To enroll in a new bootcamp,
                please contact your administrator to adjust your limit.
              </p>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════
            LOADING SKELETONS
        ══════════════════════════════════════ */}
        {loading && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '20px',
          }}>
            {Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        )}

        {/* ══════════════════════════════════════
            ERROR STATE
        ══════════════════════════════════════ */}
        {!loading && error && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '64px 24px',
            gap: '16px',
            textAlign: 'center',
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '16px',
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <ExclamationTriangleIcon style={{ width: '26px', height: '26px', color: '#DC2626' }} />
            </div>
            <div>
              <p style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A', marginBottom: '4px' }}>
                Something went wrong
              </p>
              <p style={{ fontSize: '13px', color: '#64748B', margin: '0 0 20px' }}>{error}</p>
              <button
                onClick={fetchEnrollments}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '7px',
                  padding: '10px 20px',
                  borderRadius: '12px',
                  border: '1.5px solid #E2E8F0',
                  background: '#FFFFFF',
                  color: '#475569',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 150ms',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#F8FAFC'; e.currentTarget.style.color = '#0F172A'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.color = '#475569'; }}
              >
                <ArrowPathIcon style={{ width: '15px', height: '15px' }} />
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════
            EMPTY STATE
        ══════════════════════════════════════ */}
        {!loading && !error && enrollments.length === 0 && (
          <EmptyState onExplore={() => navigate('/bootcamp/enrollment')} />
        )}

        {/* ══════════════════════════════════════
            BOOTCAMP CARDS GRID
        ══════════════════════════════════════ */}
        {!loading && !error && enrollments.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '20px',
          }}>
            {enrollments.map((enrollment) => (
              <BootcampCard
                key={enrollment.enrollment_id}
                enrollment={enrollment}
                onOpenDashboard={handleOpenDashboard}
                onSetActive={handleSetActive}
              />
            ))}
          </div>
        )}
      </div>

      {/* Spin keyframe for the loading spinner */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}


